import { SimpleGrid } from "@chakra-ui/react";
import { Product } from "@/types/product";
import ProductCard from "./ProductCard";
import { Button } from "../ui/button";
import { EmptyState } from "../ui/empty-state";
import { HiColorSwatch } from "react-icons/hi";
import React, { forwardRef } from "react";
import type { SimpleGridProps } from "@chakra-ui/react";
import { I18nLink } from "../i18n/I18nLink";
import { useTranslations } from "next-intl";

interface ProductListProps extends Omit<SimpleGridProps, "children"> {
  products: Product[];
  bordered?: boolean;
  cardProps?: Omit<React.ComponentProps<typeof ProductCard>, "product">;
}

export default forwardRef<HTMLDivElement, ProductListProps>(function ProductList(
  { products, bordered = true, cardProps, ...gridProps }: ProductListProps,
  ref
) {
  const t = useTranslations()
  if (!products || products.length === 0) {
    return (
      <EmptyState
        icon={<HiColorSwatch />}
        title={t('no_products')}
        description={t('try_change_filters_or_back')}
      >
        <I18nLink href="/" passHref>
          <Button borderRadius={"none"}>{t('go_to_mainpage')}</Button>
        </I18nLink>
      </EmptyState>
    );
  }

  const getBorders = (index: number, total: number) => {
    const baseCols = 2, mdCols = 3, lgCols = 4;

    const get = (cols: number) => {
      const rowCount = Math.ceil(total / cols);
      const rowIndex = Math.floor(index / cols);
      const colIndex = index % cols;
      const isLastCol = colIndex === cols - 1;
      const hasItemBelow = index + cols < total;
    
      if (!bordered) {
        return {};
      }

      return {
        borderTop: "1px solid #CDCDCD",
        borderRight: !isLastCol ? "1px solid #CDCDCD" : undefined,
        borderBottom: !hasItemBelow ? "1px solid #CDCDCD" : undefined,
        borderLeft: undefined,
      };
    };
    
    return {
      borderTop: {
        base: get(baseCols).borderTop,
        md: get(mdCols).borderTop,
        lg: get(lgCols).borderTop,
      },
      borderRight: {
        base: get(baseCols).borderRight,
        md: get(mdCols).borderRight,
        lg: get(lgCols).borderRight,
      },
      borderBottom: {
        base: get(baseCols).borderBottom,
        md: get(mdCols).borderBottom,
        lg: get(lgCols).borderBottom,
      },
      borderLeft: {
        base: get(baseCols).borderLeft,
        md: get(mdCols).borderLeft,
        lg: get(lgCols).borderLeft,
      },
    };
  };

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={0} ref={ref} {...gridProps}>
      {products.map((product, index) => {
        const borders = getBorders(index, products.length);

        // ? Переделать стили сетки под media queries, если не будет дерганий
        return (
          <ProductCard
            key={index}
            boxSizing={"border-box"}
            product={product}
            borderTop={borders.borderTop}
            borderRight={borders.borderRight}
            borderBottom={borders.borderBottom}
            // borderLeft={borders.borderLeft}
            {...cardProps}
          />
        );
      })}
    </SimpleGrid>
  );
});